import React, { useEffect, useRef, useState } from 'react'
import styles from './BreakScheduleModal.module.css'
import o from '../../components/Overlay.module.css'
import { apiJson } from '../../lib/api'
import { fmtStamp } from './time'
import { fmtDate } from './BreakScheduleModal'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow, inertClass } from '../../components/pending'

// Timesheet settings (handoff §9.4, h 2900: "Whether timesheets may be edited
// on the current day"; GET/POST /api/timesheets/settings). Tenant
// configuration, so every change is kept with who made it. "Today" is the
// business's date, not this browser's.

export interface TimesheetSettings {
  today: string
  edit_current_day: boolean
  history: { at: string; by: string | null; by_email?: string | null; changes: Record<string, { from: unknown; to: unknown }>; reason?: string | null }[]
  updated_at: string | null
  updated_by: string | null
}

interface Props {
  user: any
  onClose: () => void
  onSaved: (flash: string) => void
}

const onOff = (v: unknown) => (v === false ? 'not allowed' : 'allowed')

export function TimesheetSettingsModal({ user, onClose, onSaved }: Props) {
  const [s, setS] = useState<TimesheetSettings | null>(null)
  const [editToday, setEditToday] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const userRef = useRef(user)
  userRef.current = user
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once the
  // switch has moved from the setting that was read. Nothing closes it while
  // its write is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(editToday, !!s)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: !!s, busy })

  useEffect(() => {
    apiJson<TimesheetSettings>(userRef.current, '/api/timesheets/settings')
      .then((r) => {
        setS(r)
        setEditToday(r.edit_current_day !== false)
      })
      .catch((e) => setError(e?.message || 'Could not load the timesheet settings'))
  }, [])

  async function save() {
    if (!s || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<TimesheetSettings & { changed: string[] }>(user, '/api/timesheets/settings', { body: { edit_current_day: editToday } })
      onSaved(!r.changed?.length ? 'Timesheet settings unchanged' : editToday ? 'Today’s timesheets can be edited' : 'Today’s timesheets can no longer be edited')
    } catch (e: any) {
      setError(e?.message || 'Could not save the timesheet settings')
      setBusy(false)
    }
  }

  const history = (s?.history || []).slice().reverse()

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={panelEl} tabIndex={-1} className={o.modal} role="dialog" aria-label="Timesheet settings">
          <div className={o.head}>
            <div className={o.headText}>
              <h2 className={o.title}>Timesheet settings</h2>
              <p className={o.sub}>How this business corrects its timesheets. These apply to everyone who edits them.</p>
            </div>
            <button type="button" className={o.close} aria-label="Close" onClick={requestClose} disabled={busy}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={`${o.body} ${inertClass(busy, slow)}`}>
            {s && (
              <>
                <div className={o.toggleRow}>
                  <div className={o.toggleText}>
                    <div className={o.toggleTitle}>Allow edits to today’s timesheets</div>
                    <div className={o.toggleSub}>
                      {editToday
                        ? 'Shifts on today’s date can be added, corrected and excluded while the day is still being worked.'
                        : `Today’s shifts (${fmtDate(s.today)}) cannot be added, corrected or excluded until the day is over. Comments can still be added, and Clock all off still ends the day.`}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={editToday}
                    aria-label="Allow edits to today’s timesheets"
                    className={o.switch}
                    onClick={() => setEditToday((v) => !v)}
                  />
                </div>
                <p className={o.fieldHint}>“Today” is the business’s date in its own time zone. Earlier days are governed by the day state as before: a locked day refuses edits either way.</p>

                <div className={o.section}>
                  <p className={o.label}>Change history</p>
                  {history.length === 0 ? (
                    <p className={o.fieldHint}>Never changed: the default, edits allowed, is in force.</p>
                  ) : (
                    <div className={styles.versions}>
                      {history.map((h, i) => (
                        <div key={h.at + i} className={styles.version} data-testid="ts-setting-change">
                          <div className={styles.versionText}>
                            <div className={styles.versionHead}>
                              <span className={styles.versionLabel}>{fmtStamp(h.at)}</span>
                            </div>
                            <div className={styles.versionSummary}>
                              {h.changes.edit_current_day
                                ? `Edits to today: ${onOff(h.changes.edit_current_day.from)} → ${onOff(h.changes.edit_current_day.to)}`
                                : Object.keys(h.changes).join(', ')}
                              {h.by_email || h.by ? ` · by ${h.by_email || h.by}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {error && (
              <div className={o.danger} role="alert">
                {error}
              </div>
            )}
          </div>

          <div className={o.foot}>
            <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
              Cancel
            </button>
            <PendingButton type="button" className={o.primary} onClick={save} disabled={!s} busy={busy} pending="Saving…">
              Save settings
            </PendingButton>
          </div>
        </div>
      </div>
      {confirm}
    </>
  )
}
